import { emptyPaginationState } from "@/cache/EmptyValues";
import merge from "lodash/merge";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

/**
 * Zustand store for pagination state
 * Replaces the Apollo Cache implementation
 */
type State = {
  paginationState: PaginationState;
}

type Actions = {
  updatePaginationState: (_dataPayload: PartialDeep<PaginationState>) => void;
  resetPaginationState: () => void;
  resetPaginationField: (_field: keyof PaginationState) => void;
}

export const usePaginationStore = create<State & Actions>()(
  persist(
    (set) => ({
      paginationState: emptyPaginationState,

      /**
       * Update pagination state with new values
       * @param dataPayload Partial pagination state to merge with current state
       */
      updatePaginationState: (dataPayload: PartialDeep<PaginationState>) =>
        set((state) => {
          // List of paginated fields that have filters that should be overwritten
          const paginatedFieldsWithFilters: (keyof PaginationState)[] = [
            "paginatedTenders",
            "paginatedTariffs",
            "paginatedHolidays",
            "paginatedSocialSecurity",
            "paginatedLiabilityInsurance",
            "paginatedVacationBonus",
            "paginatedChristmasBonus",
            "paginatedVacationDays"
          ];

          // Check if any paginated field has filters to overwrite
          const fieldWithFilters = paginatedFieldsWithFilters.find(field =>
            dataPayload[field] && "filters" in dataPayload[field]! && dataPayload[field]!.filters
          );

          if (fieldWithFilters) {
            const newState = { ...state.paginationState };
            const fieldData = newState[fieldWithFilters] as any;
            const payloadData = dataPayload[fieldWithFilters] as any;

            // Initialize filters if they don't exist
            if (!fieldData.filters) {
              fieldData.filters = {};
            }

            // Replace the entire filters object to handle field removal
            const newFilters = payloadData.filters;
            fieldData.filters = newFilters;

            // Also handle sort field if it's provided in the payload
            if ("sort" in payloadData) {
              fieldData.sort = payloadData.sort;
            }

            return { paginationState: newState };
          }

          // For all other updates, use lodash merge for deep updates
          const newState = merge({}, state.paginationState, dataPayload);
          return { paginationState: newState };
        }),

      /**
       * Reset pagination state to empty values
       */
      resetPaginationState: () => {
        set({ paginationState: emptyPaginationState });
      },

      /**
      * Reset a specific field in the pagination state to its empty value
      * @param field The field to reset
      */
      resetPaginationField: (field: keyof PaginationState) =>
        set((state) => {
          // Return the updated state with the specific field reset
          return {
            paginationState: {
              ...state.paginationState,
              [field]: emptyPaginationState[field]
            }
          };
        }),
    }),
    {
      name: "pagination-storage",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        paginationState: state.paginationState,
      }),
      merge: (persistedState, currentState) => {
        /**
         * Temp simple shortcut method of migrating existing customerName field to search field.
         * Use migrate and versioning for proper schema migrations if this becomes a recurring change.
         * TODO: Remove this later as this will become bloat code once prod user has new schema.
         * Important for UX so users dont think data is lost when active filters not updated and
         * returning empty results.
         */
        const persisted = persistedState as State;
        const filters = persisted?.paginationState?.paginatedTenders?.filters as Record<string, unknown> | undefined;

        if (filters && "customerName" in filters) {
          filters.search ??= filters.customerName ?? null;
          delete filters.customerName;
        }

        return { ...currentState, ...persisted };
      },
    }
  )
);

/**
 * Hook to get pagination state for a specific field
 * Replacement for the Apollo Cache usePaginationState hook
 * @param paginationField The field to get from pagination state
 * @returns The pagination state for the specified field
 */
export function usePaginationState<K extends keyof PaginationState>(paginationField: K): PaginationState[K] {
  const paginationState = usePaginationStore((state) => state.paginationState);
  return paginationState[paginationField];
}

/**
 * Function to update pagination state
 * Replacement for the Apollo Cache updatePaginationState function
 * @param dataPayload Partial pagination state to merge with current state
 */
export function updatePaginationState(dataPayload: PartialDeep<PaginationState>) {
  usePaginationStore.getState().updatePaginationState(dataPayload);
}

/**
 * Function to reset a specific field in the pagination state
 * @param field The field to reset (e.g., 'paginatedTenders')
 */
export function resetPaginationField(field: keyof PaginationState) {
  usePaginationStore.getState().resetPaginationField(field);
}
